<template>
  <Wrapper class="app app--auth">
    <div class="app-content">
      <div class="block block-shadow py-30 block-rounded">
        <header class="block-header text-center" v-if="isDefaultLogo != ''">
          <img :src="isDefaultLogo" alt="brand logo" />
        </header>
        <div>
          <div class="block-content p-0">
            <h1 class="block-title text-center">
              {{ pageTitle }}
            </h1>
          </div>
          <div v-if="info" class="block-content">
            <div class="alert alert-info">
              {{ info }}
            </div>

            <div class="text-center">
              <router-link to="/" class="btn btn-gd-primary">
                Sign In
              </router-link>
            </div>
          </div>
          <div v-if="errors.length" class="block-content">
            <div
              v-for="(error, index) in errors"
              class="alert alert-danger"
              :key="index"
            >
              {{ error.message }}
              Click
              <router-link to="/forgot">here</router-link> to request a new one.
            </div>
          </div>
          <div v-if="!errors.length" class="block-content">
            <form
              v-if="showForm"
              class="form"
              @submit.prevent="handleFormSubmit"
            >
              <div class="form-group">
                <div class="form-material pt-0">
                  <input
                    type="password"
                    v-model="password"
                    name="password"
                    id="password"
                    class="form-control no-focus"
                    placeholder="Enter new password"
                    v-validate="'required|min:6|max:100'"
                    data-vv-as="password"
                    ref="password"
                  />
                  <div
                    v-if="veeErrors.first('password')"
                    class="invalid-feedback"
                  >
                    {{ veeErrors.first("password") }}
                  </div>
                  <label for="password" class="sr-only">Password</label>
                </div>
              </div>
              <div class="form-group mb-30">
                <div class="form-material pt-0">
                  <input
                    type="password"
                    v-model="confirmPassword"
                    name="confirmPassword"
                    id="confirmPassword"
                    class="form-control no-focus"
                    placeholder="Confirm new password"
                    v-validate="'required|confirmed:password|min:6|max:100'"
                    data-vv-as="confirm password"
                  />
                  <div
                    v-if="veeErrors.first('confirmPassword')"
                    class="invalid-feedback"
                  >
                    {{ veeErrors.first("confirmPassword") }}
                  </div>
                  <label for="password" class="sr-only">Confirm Password</label>
                </div>
              </div>
              <div class="form-group">
                <button
                  type="submit"
                  class="btn btn-gd btn-gd-primary btn-block"
                  style="color: #fff"
                  :disabled="isLoading"
                >
                  <Spinner v-if="isLoading" theme="currentColor" />
                  <span v-else>Save</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </Wrapper>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import serializeError from "@/helpers/serializeError";
import axios from "@/helpers/base";
import Wrapper from "./Wrapper";
import Spinner from "@/components/Spinner";
import authTypes from "@/store/auth/types";
import { socketSender } from "@/helpers/socket";
import { confirmPasswordResetMailer } from "@/helpers/mailer";
import _ from "lodash";

export default {
  name: "Reset",
  components: {
    Wrapper,
    Spinner
  },
  async beforeRouteEnter(to, from, next) {
    // Redirect immediately if route does not contain token query.
    if (!to.query.token) {
      next(vm => {
        vm.$router.replace("/forgot");
      });
    }

    try {
      // Set state for matched token in our database.
      const {
        data: { data }
      } = await axios.get(`/auth/reset/${to.query.token}`);

      next(vm => {
        vm.id = data.id;
        vm.email = data.email;
        vm.displayName = data.displayName;
        vm.token = to.query.token;
      });
    } catch (error) {
      next(vm => {
        if (error.response) {
          return (vm.errors = error.response.data.errors);
        }
        vm.errors.push(serializeError(error));
      });
    }
  },
  data() {
    return {
      error: null,
      isLoading: false,
      errors: [],
      validations: [],
      info: "",
      token: "",
      email: "",
      displayName: "",
      id: null,
      password: "",
      confirmPassword: "",
      showForm: true,
      pageTitle: "Password Reset",
      isDefaultLogo: "",
      headroomLogo: require("@/assets/media/brand.svg")
    };
  },
  async mounted() {
    await this.$store.dispatch("fetchOptions");

    let defaultLogoStatus = _.filter(this.options, ["key", "defaultlogo.main"]);
    if (
      defaultLogoStatus.length &&
      defaultLogoStatus[0].value.status == false
    ) {
      let defaultLogo = _.filter(this.options, ["key", "defaultlogo.sub"]);
      if (defaultLogo.length && defaultLogo[0].value.image) {
        this.isDefaultLogo = defaultLogo[0].value.image;
      } else {
        this.isDefaultLogo = this.headroomLogo;
      }
    } else {
      this.isDefaultLogo = this.headroomLogo;
    }
  },
  computed: {
    options() {
      return this.$store.state.concierge.optionsList.data;
    }
  },
  methods: {
    ...mapActions({
      AUTH_SET: "AUTH_SET",
      AUTH_CHECK: "AUTH_CHECK"
    }),
    ...mapGetters({
      isLoggedIn: "isLoggedIn",
      getUser: "getUser"
    }),
    async handleFormSubmit() {
      this.isLoading = true;
      this.errors = [];
      let isFormValid = await this.$validator.validateAll();

      if (!isFormValid) {
        this.isLoading = false;
        return;
      }

      try {
        const { status } = await axios.post(`/auth/reset/${this.id}`, {
          password: this.password
        });

        await confirmPasswordResetMailer({
          to: this.email,
          displayName: this.displayName,
          tenantName: ""
        });

        if (status !== 200) {
          throw new Error("Unable to process request. Try again.");
        }

        this.showForm = false;
        this.pageTitle = "Thank You!";
        this.info = "Please sign in with your new password.";
        this.isLoading = false;
      } catch (error) {
        this.isLoading = false;
        if (error.response) {
          return (this.errors = error.response.data.errors);
        }
        this.$toastr.error(error.message, "Oops!");
      }
    },
    async checkForm() {
      this.validations = [];

      if (this.password.length < 6) {
        this.validations.push({
          message: "Password should be atleast 6 characters."
        });
      }

      if (this.password !== this.confirmPassword) {
        this.validations.push({ message: "Password did not match." });
      }
    },
    userLogin() {
      this.isLoading = true;

      const user = {
        email: this.email,
        password: this.password
      };

      return this.AUTH_SET(user)
        .then(response => {
          this.$store.dispatch(authTypes.AUTH_SET_ERRORS, []).then(() => {
            if (!response) {
              return;
            }

            const roles = response.Roles.map(item => item.value);
            this.email = null;
            this.password = null;
            let dashboard = {};

            if (roles.includes(8)) {
              dashboard.name = "Admin Dashboard";
              dashboard.route = "/admin/overview";
            } else if (roles.includes(2)) {
              dashboard.name = "Concierge Dashboard";
              dashboard.route = "/concierge/dashboard";
            } else {
              dashboard.name = "User Dashboard";
              dashboard.route = "/user/dashboard";
            }

            /**
             * SOCKET: User Login to QSpace
             */
            let logContent = `${response.firstName} ${response.lastName} signed in on the QSpace ${dashboard.name}`;
            socketSender("userLogin", logContent).then(() => {
              /**
               * Redirect to their assigned dashboard
               */
              window.location.href = dashboard.route;
            });
          });
        })
        .catch(() => {
          this.error = "Invalid Credentials";
        })
        .finally(() => {
          this.isLoading = false;
        });
    }
  }
};
</script>

<style lang="scss"></style>
